import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import sellarModule from '../../api/modules/seller.module';
import { track } from '../../analytics/track';
import { EVENTS } from '../../analytics/events';
import { useAuth } from '../../hooks/useAuth';
import { showToast } from '../../utils/toast';

import SavedTable from '../BuyerDashboard/SavedTable';
import { BuyerStatsPanel } from '../BuyerDashboard/BuyerDashboard';
import OrderHistoryTable from '../BuyerDashboard/OrderHistoryTable';
import ListingTable from './ListingTable/ListingTable';
import OrderTable from './OrderTable/OrderTable';
import RecentReviews from './RecentReviews/RecentReviews';
import TransectionTable from './TransectionTable/TransectionTable';
import { SellerDashboardView } from './SellerDashboard.view';

/**
 * `/dashboard` for an approved seller. The container.
 *
 * It owns exactly what it owned before: the mode, the inner tab, the
 * `SELLER_DASHBOARD_VIEWED` event fired once, the dashboard-stats fetch and the
 * store-availability toggle. Every request, param name and response handler is
 * unchanged, including `getSellerDashboardStats({ index: 0, offset: 10 })`,
 * which pages a list this screen never renders and is preserved verbatim rather
 * than "tidied" into a call the backend has not been asked for.
 *
 * The tables stay self-fetching containers and arrive here as a NODE, which is
 * what lets the view be pure: the design-system explorer hands the same view a
 * `<ListingTableView>` with fixture rows instead.
 *
 * WHAT DIED: `Views/SellerView.jsx`, `Views/BuyerView.jsx`, `Views/Views.css`,
 * `Tabs/Tabe.jsx`, `Tabs/tabs.css`, `config/tabsConfig.js` (whose `content`
 * fields held emoji placeholders that were never rendered),
 * `TwoColumnLayout/`, `DashboardHeader/`, `Layout/Layout.jsx`,
 * `NavigationHeader/`, `UserDropDown/` and `SellerDashboard.css`.
 */
/**
 * Which mode owns a tab id. `order_history` and `saved` are the BUYER tabs, so
 * a deep link to one has to flip the mode too, or the page selects a tab that
 * is not in the row it is showing and silently falls back to the first one.
 */
const BUYER_OWNED_TABS = ['saved', 'order_history'];
const SELLER_OWNED_TABS = ['transactions', 'listings', 'orders'];

/** Read `?tab=` once, and derive the mode it implies. */
function readTabFromSearch(search) {
  const requested = new URLSearchParams(search).get('tab');
  if (!requested) return null;
  if (BUYER_OWNED_TABS.includes(requested)) return { mode: 'buyer_view', tab: requested };
  if (SELLER_OWNED_TABS.includes(requested)) return { mode: 'seller_view', tab: requested };
  return null;
}

const SellerDashboard = () => {
  const location = useLocation();
  /* `?tab=` is honoured on first render rather than in an effect, so the page
     never paints the wrong tab and then jump to the right one.

     This was a real dead link: the nav's "Orders" points at
     `/dashboard?tab=order_history`, and `SellerDashboard` hardcoded
     `'transactions'` and never looked at the query at all. A SELLER clicking
     Orders therefore landed on Seller view / Transactions — the buyer's order
     history was simply unreachable from the nav for anyone who sells.
     `BuyerDashboard` had always read the param, so buyers were fine and the
     break only showed for sellers. The pre-conversion dashboard read it too. */
  const deepLink = readTabFromSearch(location.search);
  const [mode, setMode] = useState(deepLink?.mode || 'seller_view');
  const [tab, setTab] = useState(deepLink?.tab || 'transactions');
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [storeUnavailable, setStoreUnavailable] = useState(false);
  const [availabilityBusy, setAvailabilityBusy] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();
  const viewedTracked = useRef(false);

  useEffect(() => {
    if (viewedTracked.current) return;
    const props = { viewed_at: new Date().toISOString() };
    const sellerProfileId = user?.id?.toString?.() || user?.user_id;
    if (sellerProfileId) props.seller_profile_id = sellerProfileId;
    track(EVENTS.SELLER_DASHBOARD_VIEWED, props);
    viewedTracked.current = true;
  }, [user]);

  /* And again on every later navigation, because clicking Orders while already
     on /dashboard changes the query without remounting the component, so the
     initial state above never re-runs. Keyed on `location.search` alone: it
     must not fight the user's own tab clicks, which do not touch the URL. */
  useEffect(() => {
    const next = readTabFromSearch(location.search);
    if (!next) return;
    setMode(next.mode);
    setTab(next.tab);
  }, [location.search]);

  // Switching mode resets to that mode's first tab, exactly as before.
  const handleModeChange = useCallback((next) => {
    setMode(next);
    setTab(next === 'buyer_view' ? 'saved' : 'transactions');
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchMetrics = async () => {
      setMetricsLoading(true);
      const res = await sellarModule.getSellerDashboardStats({
        index: 0,
        offset: 10,
      });

      if (cancelled) return;

      if (res.success) {
        const apiData = res.data;

        setStoreUnavailable(apiData.is_store_unavailable || false);

        setMetrics({
          totalRevenue: {
            value: apiData.total_revenue.value,
            change: apiData.total_revenue.change_percent,
            trend: apiData.total_revenue.trend,
          },
          activeListings: {
            value: apiData.active_listings.value,
            change: apiData.active_listings.change_percent,
            trend: apiData.active_listings.trend,
          },
          totalOrders: {
            value: apiData.total_orders.value,
            change: apiData.total_orders.change_percent,
            trend: apiData.total_orders.trend,
          },
          customerRating: {
            value: apiData.customer_rating.value,
            change: apiData.customer_rating.change_percent,
            trend: apiData.customer_rating.trend,
          },
        });
      } else {
        console.error(res.error);
      }
      setMetricsLoading(false);
    };

    fetchMetrics();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAvailabilityChange = async (unavailable) => {
    const prevValue = storeUnavailable;
    setAvailabilityBusy(true);
    setStoreUnavailable(unavailable);
    try {
      const res = await sellarModule.updateStoreAvailability(unavailable);
      if (!res.success) {
        throw new Error(res.message);
      }
      showToast.success(`Store marked as ${unavailable ? 'unavailable' : 'available'}`);
    } catch (error) {
      console.error('Failed to update store availability:', error);
      setStoreUnavailable(prevValue);
      showToast.error(error.message || 'Failed to update store availability');
    } finally {
      setAvailabilityBusy(false);
    }
  };

  const renderPanel = () => {
    switch (tab) {
      case 'listings':
        return <ListingTable />;
      case 'orders':
        return <OrderTable />;
      case 'saved':
        return <SavedTable />;
      case 'order_history':
        return <OrderHistoryTable />;
      case 'transactions':
      default:
        return <TransectionTable />;
    }
  };

  return (
    <SellerDashboardView
      mode={mode}
      onModeChange={handleModeChange}
      tab={tab}
      onTabChange={setTab}
      metrics={metrics}
      metricsLoading={metricsLoading}
      storeUnavailable={storeUnavailable}
      availabilityBusy={availabilityBusy}
      onAvailabilityChange={handleAvailabilityChange}
      reviews={<RecentReviews />}
      /* Mounted only in buyer mode by the view, so the collection summary is
         not fetched for a seller who never switches across. */
      buyerStats={mode === 'buyer_view' ? <BuyerStatsPanel /> : null}
      panel={renderPanel()}
      onNewListing={() => navigate('/create-listing')}
      onExploreMarketplace={() => navigate('/marketplace')}
    />
  );
};

export default SellerDashboard;
